/**
 * RecoveryLeadsAdmin — /admin/recovery-leads
 *
 * Lista de emails que intentaron pagar (Stripe + MercadoPago) para recovery
 * futuro. Fuente: premium_checkout_intents vía GET /api/admin/recovery-leads.
 */

import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  CheckCircle2,
  Copy,
  Download,
  ExternalLink,
  Loader2,
  Mail,
  RefreshCw,
  Search,
  ShieldCheck,
  Users,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

type DirectoryRow = {
  customer_email: string;
  customer_name: string | null;
  providers: string[];
  attempt_count: number;
  statuses: string[];
  countries: string[];
  last_provider: string | null;
  last_flow_name: string | null;
  last_plan_tier: string | null;
  last_amount: number | null;
  last_currency: string | null;
  last_landing_path?: string | null;
  last_parent_template?: string | null;
  last_external_id?: string | null;
  first_seen_at: string;
  last_seen_at: string;
  recovery_email_sent: boolean;
  ever_completed: boolean;
  recovery_checkout_provider: "stripe";
  brand_source?: string;
  /** Site funnel flows (dual_cta_video, …) — not cron-only recovery. */
  site_flows?: string[];
  site_confirmed?: boolean;
  site_attempt_count?: number;
};

type RecoveryLeadsResponse = {
  generated_at: string;
  days: number;
  only_recoverable: boolean;
  brand_source?: string;
  isolation?: string;
  recovery_checkout_provider: string;
  note?: string;
  squad?: {
    name: string;
    mission: string;
    roles: string[];
  };
  totals: {
    unique_emails: number;
    recoverable_now_2_24h: number;
    site_confirmed?: number;
    site_confirmed_recoverable?: number;
    by_provider_on_directory: {
      stripe: number;
      mercadopago: number;
      other: number;
    };
    by_country?: Record<string, number>;
  };
  recoverable_window: {
    raw: number;
    after_dedupe: number;
    leads: Array<{
      customer_email: string;
      provider: string;
      country: string | null;
      flow_name: string | null;
      status: string;
      created_at: string;
    }>;
  };
  directory: DirectoryRow[];
};

type ListFilter =
  | "all"
  | "recoverable"
  | "site_confirmed"
  | "site_recoverable"
  | "completed"
  | "mailed";

function fmtTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString("es-AR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function fmtAmount(amount: number | null, currency: string | null, provider: string | null): string {
  if (amount == null) return "—";
  const c = (currency || "USD").toUpperCase();
  const value = provider === "stripe" || c === "USD" ? amount / 100 : amount;
  try {
    return new Intl.NumberFormat("es-AR", { style: "currency", currency: c }).format(value);
  } catch {
    return `${value} ${c}`;
  }
}

function escCsv(v: unknown): string {
  if (v == null) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function isRecoverableRow(r: DirectoryRow): boolean {
  return (
    !r.ever_completed &&
    !r.recovery_email_sent &&
    r.statuses.some((s) =>
      ["started", "expired", "failed", "abandoned"].includes(s),
    )
  );
}

export default function RecoveryLeadsAdmin() {
  const { getAuthHeaders } = useAdminAuth();
  const { toast } = useToast();

  const [data, setData] = useState<RecoveryLeadsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState("90");
  /** Default: only leads proven from this site that still need recovery (admin sends mail manually). */
  const [listFilter, setListFilter] = useState<ListFilter>("site_recoverable");
  const [search, setSearch] = useState("");
  const [providerFilter, setProviderFilter] = useState<"all" | "stripe" | "mercadopago">(
    "all",
  );
  const [countryFilter, setCountryFilter] = useState<string>("all");
  /** Last minted checkout URL per email (session) — admin copies and sends by hand. */
  const [mintedUrls, setMintedUrls] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      // always load full directory; filter client-side for UX
      const res = await fetch(
        `/api/admin/recovery-leads?days=${encodeURIComponent(days)}&limit=2000`,
        { headers },
      );
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`HTTP ${res.status} — ${body.slice(0, 200)}`);
      }
      setData((await res.json()) as RecoveryLeadsResponse);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [days, getAuthHeaders]);

  useEffect(() => {
    void load();
  }, [load]);

  const [mintingEmail, setMintingEmail] = useState<string | null>(null);

  const filtered = useMemo(() => {
    let rows = data?.directory ?? [];
    if (listFilter === "recoverable") rows = rows.filter(isRecoverableRow);
    if (listFilter === "site_confirmed") rows = rows.filter((r) => r.site_confirmed);
    if (listFilter === "site_recoverable") {
      rows = rows.filter((r) => r.site_confirmed && isRecoverableRow(r));
    }
    if (listFilter === "completed") rows = rows.filter((r) => r.ever_completed);
    if (listFilter === "mailed") rows = rows.filter((r) => r.recovery_email_sent);
    if (providerFilter !== "all") {
      rows = rows.filter((r) => r.providers.includes(providerFilter));
    }
    if (countryFilter !== "all") {
      rows = rows.filter((r) =>
        r.countries.some((c) => c.toUpperCase() === countryFilter),
      );
    }
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(
        (r) =>
          r.customer_email.includes(q) ||
          (r.customer_name || "").toLowerCase().includes(q) ||
          (r.last_flow_name || "").toLowerCase().includes(q) ||
          (r.last_landing_path || "").toLowerCase().includes(q) ||
          (r.last_parent_template || "").toLowerCase().includes(q) ||
          (r.site_flows || []).some((f) => f.toLowerCase().includes(q)) ||
          r.countries.some((c) => c.toLowerCase().includes(q)),
      );
    }
    return rows;
  }, [data, listFilter, providerFilter, countryFilter, search]);

  const countryOptions = useMemo(() => {
    const map = data?.totals.by_country || {};
    return Object.keys(map).sort((a, b) => (map[b] || 0) - (map[a] || 0));
  }, [data]);

  const stats = useMemo(() => {
    const dir = data?.directory ?? [];
    return {
      total: dir.length,
      recoverable: dir.filter(isRecoverableRow).length,
      siteConfirmed: dir.filter((r) => r.site_confirmed).length,
      siteRecoverable: dir.filter((r) => r.site_confirmed && isRecoverableRow(r)).length,
      completed: dir.filter((r) => r.ever_completed).length,
      mailed: dir.filter((r) => r.recovery_email_sent).length,
      stripe: dir.filter((r) => r.providers.includes("stripe")).length,
      mp: dir.filter((r) => r.providers.includes("mercadopago")).length,
      windowNow: data?.totals.recoverable_now_2_24h ?? 0,
    };
  }, [data]);

  /**
   * Mint Stripe recovery Checkout URL only.
   * NEVER sends email — admin copies link and writes the buyer manually.
   */
  const mintRecoveryLink = async (row: DirectoryRow) => {
    setMintingEmail(row.customer_email);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/recover-checkout", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          buyerEmail: row.customer_email,
          planTier: row.last_plan_tier === "vip" || !row.last_plan_tier ? "vip" : row.last_plan_tier,
          currency: "USD",
          country: row.countries[0] || "US",
          // Hard requirement: no Resend / no recovery mail from this path.
          skipRecoveryEmail: true,
          flowName: "admin_recovery_mint",
        }),
      });
      const body = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        checkoutUrl?: string;
        error?: string;
        emailSent?: boolean;
      };
      if (!res.ok || !body.checkoutUrl) {
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      if (body.emailSent) {
        // Defensive: recover-checkout must never mail when skipRecoveryEmail=true
        console.error("[RecoveryLeadsAdmin] unexpected emailSent=true");
      }
      setMintedUrls((prev) => ({ ...prev, [row.customer_email]: body.checkoutUrl! }));
      await navigator.clipboard.writeText(body.checkoutUrl);
      toast({
        title: "Link listo — sin email automático",
        description:
          "URL copiada. Pegala en tu mail/WhatsApp manual. El sistema NO envió nada al comprador.",
      });
    } catch (e) {
      toast({
        title: "No se pudo generar link",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setMintingEmail(null);
    }
  };

  const copyOneEmail = async (email: string) => {
    try {
      await navigator.clipboard.writeText(email);
      toast({ title: "Email copiado", description: email });
    } catch {
      toast({ title: "No se pudo copiar", variant: "destructive" });
    }
  };

  const copyMintedUrl = async (email: string) => {
    const url = mintedUrls[email];
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "Link copiado de nuevo" });
    } catch {
      toast({ title: "No se pudo copiar", variant: "destructive" });
    }
  };

  const copyEmails = async () => {
    const emails = filtered.map((r) => r.customer_email).join("\n");
    if (!emails) {
      toast({ title: "Nada que copiar", variant: "destructive" });
      return;
    }
    try {
      await navigator.clipboard.writeText(emails);
      toast({
        title: "Emails copiados",
        description: `${filtered.length} dirección(es) en el portapapeles`,
      });
    } catch {
      toast({ title: "No se pudo copiar", variant: "destructive" });
    }
  };

  const downloadCsv = () => {
    const headers = [
      "customer_email",
      "customer_name",
      "brand_source",
      "providers",
      "attempt_count",
      "statuses",
      "countries",
      "last_provider",
      "last_flow_name",
      "last_landing_path",
      "last_parent_template",
      "last_plan_tier",
      "last_amount",
      "last_currency",
      "first_seen_at",
      "last_seen_at",
      "ever_completed",
      "recovery_email_sent",
      "is_recoverable",
      "site_confirmed",
      "site_flows",
      "site_attempt_count",
      "recovery_checkout_provider",
    ];
    const lines = [headers.join(",")];
    for (const r of filtered) {
      lines.push(
        [
          r.customer_email,
          r.customer_name,
          r.brand_source || data?.brand_source || "",
          r.providers.join("|"),
          r.attempt_count,
          r.statuses.join("|"),
          r.countries.join("|"),
          r.last_provider,
          r.last_flow_name,
          r.last_landing_path,
          r.last_parent_template,
          r.last_plan_tier,
          r.last_amount,
          r.last_currency,
          r.first_seen_at,
          r.last_seen_at,
          r.ever_completed,
          r.recovery_email_sent,
          isRecoverableRow(r),
          r.site_confirmed ?? false,
          (r.site_flows || []).join("|"),
          r.site_attempt_count ?? 0,
          r.recovery_checkout_provider,
        ]
          .map(escCsv)
          .join(","),
      );
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `recovery-leads-${days}d-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "CSV descargado", description: `${filtered.length} filas` });
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <AdminPageHeader
        description="Solo lectura + generar link. El admin envía el mail a mano. Nunca hay envío automático desde esta pantalla."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
              {loading ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              )}
              Actualizar
            </Button>
            <Button variant="outline" size="sm" onClick={() => void copyEmails()} disabled={!filtered.length}>
              <Copy className="mr-1.5 h-3.5 w-3.5" />
              Copiar emails
            </Button>
            <Button size="sm" onClick={downloadCsv} disabled={!filtered.length}>
              <Download className="mr-1.5 h-3.5 w-3.5" />
              Export CSV
            </Button>
          </div>
        }
      />

      <Card className="border-amber-500/40 bg-amber-500/[0.05]">
        <CardContent className="space-y-2 pt-4 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="bg-amber-600 hover:bg-amber-600">Manual only</Badge>
            <Badge variant="outline" className="font-mono text-[11px]">
              brand_source={data?.brand_source || "…"}
            </Badge>
            <Badge variant="outline" className="text-[11px]">
              sin email automático
            </Badge>
          </div>
          <p className="text-xs text-foreground/90 font-medium">
            1) Filtrá «Esta web · recuperable» · 2) Generá link Stripe · 3) Copiá y enviá vos el
            mensaje (Gmail / WhatsApp / Resend). El cron de recovery mail está desacoplado de
            esta UI.
          </p>
          <p className="text-[11px] text-muted-foreground">
            Badge <strong>Esta web</strong> = flow de site (dual_cta / invite). Sin ese badge no
            uses el lead para recontacto de “esta web”.
          </p>
          {data?.isolation && (
            <p className="text-[11px] text-muted-foreground border-t border-border/50 pt-2">
              {data.isolation}
            </p>
          )}
          <div className="flex flex-wrap gap-3 pt-1">
            <Button variant="link" size="sm" className="h-auto p-0" asChild>
              <Link to="/admin/buyers">Compradores (pagaron) →</Link>
            </Button>
            <Button variant="link" size="sm" className="h-auto p-0" asChild>
              <Link to="/admin/checkout-funnel">Funnel checkout →</Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-destructive/40">
          <CardContent className="pt-4 text-sm text-destructive">{error}</CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs">Emails únicos</CardDescription>
            <CardTitle className="text-2xl tabular-nums">{stats.total}</CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-emerald-500/30">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs">Esta web (confirmado)</CardDescription>
            <CardTitle className="text-2xl tabular-nums text-emerald-600">
              {stats.siteConfirmed}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card className="border-amber-500/40">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs">Esta web · recuperable</CardDescription>
            <CardTitle className="text-2xl tabular-nums text-amber-600">
              {stats.siteRecoverable}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs">Recuperables (todos)</CardDescription>
            <CardTitle className="text-2xl tabular-nums text-amber-600/80">
              {stats.recoverable}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs">Ya pagaron</CardDescription>
            <CardTitle className="text-2xl tabular-nums text-emerald-600">
              {stats.completed}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs">Mail recovery enviado</CardDescription>
            <CardTitle className="text-2xl tabular-nums">{stats.mailed}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs">Con intent Stripe</CardDescription>
            <CardTitle className="text-2xl tabular-nums">{stats.stripe}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="text-xs">Ventana cron 2–24h</CardDescription>
            <CardTitle className="text-2xl tabular-nums text-sky-600">
              {stats.windowNow}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-4 w-4" />
            Directorio de intentos
          </CardTitle>
          <CardDescription>
            {data
              ? `Últimos ${data.days} días · actualizado ${fmtTime(data.generated_at)} · mostrando ${filtered.length} de ${stats.total}`
              : "Cargando…"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <div className="relative min-w-[200px] flex-1">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar email, nombre, flow, país…"
                className="h-9 pl-8"
              />
            </div>
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="h-9 w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 días</SelectItem>
                <SelectItem value="30">30 días</SelectItem>
                <SelectItem value="90">90 días</SelectItem>
                <SelectItem value="180">180 días</SelectItem>
                <SelectItem value="365">365 días</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={listFilter}
              onValueChange={(v) => setListFilter(v as ListFilter)}
            >
              <SelectTrigger className="h-9 w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="site_recoverable">Esta web · recuperable</SelectItem>
                <SelectItem value="site_confirmed">Esta web (confirmado)</SelectItem>
                <SelectItem value="recoverable">Recuperables (todos)</SelectItem>
                <SelectItem value="completed">Completados</SelectItem>
                <SelectItem value="mailed">Ya maileados</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={providerFilter}
              onValueChange={(v) =>
                setProviderFilter(v as "all" | "stripe" | "mercadopago")
              }
            >
              <SelectTrigger className="h-9 w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos providers</SelectItem>
                <SelectItem value="stripe">Stripe</SelectItem>
                <SelectItem value="mercadopago">MercadoPago</SelectItem>
              </SelectContent>
            </Select>
            <Select value={countryFilter} onValueChange={setCountryFilter}>
              <SelectTrigger className="h-9 w-[140px]">
                <SelectValue placeholder="País" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos países</SelectItem>
                {countryOptions.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                    {data?.totals.by_country?.[c] != null
                      ? ` (${data.totals.by_country[c]})`
                      : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {loading && !data ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              No hay leads con estos filtros.
            </p>
          ) : (
            <div className="max-h-[min(70vh,720px)] overflow-auto rounded-lg border">
              <table className="w-full min-w-[1000px] text-left text-sm">
                <thead className="sticky top-0 z-10 border-b bg-muted/95 backdrop-blur">
                  <tr className="text-xs text-muted-foreground">
                    <th className="px-3 py-2 font-medium">Email</th>
                    <th className="px-3 py-2 font-medium">Nombre</th>
                    <th className="px-3 py-2 font-medium">Providers</th>
                    <th className="px-3 py-2 font-medium">Intentos</th>
                    <th className="px-3 py-2 font-medium">Estados</th>
                    <th className="px-3 py-2 font-medium">País</th>
                    <th className="px-3 py-2 font-medium">Landing / flow</th>
                    <th className="px-3 py-2 font-medium">Monto</th>
                    <th className="px-3 py-2 font-medium">Último</th>
                    <th className="px-3 py-2 font-medium">Flags</th>
                    <th className="px-3 py-2 font-medium">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const recoverable = isRecoverableRow(r);
                    const siteOk = Boolean(r.site_confirmed);
                    return (
                      <tr
                        key={r.customer_email}
                        className={cn(
                          "border-t border-border/60 hover:bg-muted/30",
                          siteOk && recoverable && "bg-emerald-500/[0.06]",
                          !siteOk && recoverable && "bg-amber-500/[0.04]",
                        )}
                      >
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            <span className="font-mono text-xs break-all">
                              {r.customer_email}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {r.customer_name || "—"}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {r.providers.map((p) => (
                              <Badge
                                key={p}
                                variant={p === "stripe" ? "default" : "secondary"}
                                className="text-[10px]"
                              >
                                {p}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-2 tabular-nums font-medium">
                          {r.attempt_count}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex max-w-[140px] flex-wrap gap-0.5">
                            {r.statuses.map((s) => (
                              <Badge key={s} variant="outline" className="text-[10px]">
                                {s}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {r.countries.length ? r.countries.join(", ") : "—"}
                        </td>
                        <td className="max-w-[180px] px-3 py-2 text-xs text-muted-foreground">
                          <div className="truncate font-mono text-[11px]" title={r.last_landing_path || ""}>
                            {r.last_landing_path || "—"}
                          </div>
                          <div className="truncate text-[10px] opacity-80">
                            {r.last_flow_name || r.last_parent_template || ""}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs tabular-nums">
                          {fmtAmount(
                            r.last_amount,
                            r.last_currency,
                            r.last_provider,
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-[11px] text-muted-foreground">
                          {fmtTime(r.last_seen_at)}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {siteOk && (
                              <Badge className="bg-emerald-600 text-[10px] hover:bg-emerald-600">
                                <ShieldCheck className="mr-0.5 h-3 w-3" />
                                Esta web
                              </Badge>
                            )}
                            {recoverable && (
                              <Badge className="bg-amber-600 text-[10px] hover:bg-amber-600">
                                recover
                              </Badge>
                            )}
                            {r.ever_completed && (
                              <Badge
                                variant="outline"
                                className="border-emerald-600/40 text-[10px] text-emerald-700"
                              >
                                <CheckCircle2 className="mr-0.5 h-3 w-3" />
                                paid
                              </Badge>
                            )}
                            {r.recovery_email_sent && (
                              <Badge variant="secondary" className="text-[10px]">
                                mailed
                              </Badge>
                            )}
                            {(r.site_flows || []).slice(0, 2).map((f) => (
                              <Badge key={f} variant="outline" className="text-[9px] font-mono">
                                {f}
                              </Badge>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-col gap-1 min-w-[140px]">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 justify-start px-2 text-[11px]"
                              onClick={() => void copyOneEmail(r.customer_email)}
                            >
                              <Copy className="mr-1 h-3 w-3" />
                              Copiar email
                            </Button>
                            {recoverable && siteOk ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-[11px]"
                                  disabled={mintingEmail === r.customer_email}
                                  onClick={() => void mintRecoveryLink(r)}
                                >
                                  {mintingEmail === r.customer_email ? (
                                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                  ) : (
                                    <ExternalLink className="mr-1 h-3 w-3" />
                                  )}
                                  Generar link
                                </Button>
                                {mintedUrls[r.customer_email] ? (
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    className="h-7 text-[11px]"
                                    onClick={() => void copyMintedUrl(r.customer_email)}
                                  >
                                    <Copy className="mr-1 h-3 w-3" />
                                    Re-copiar link
                                  </Button>
                                ) : null}
                              </>
                            ) : recoverable && !siteOk ? (
                              <span className="text-[10px] text-muted-foreground px-1">
                                sin prueba de site
                              </span>
                            ) : (
                              <span className="text-[10px] text-muted-foreground px-1">—</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
